import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { X, UserPlus, Mail, Shield, Trash2, Send } from "lucide-react";

/**
 * CollaboratorsModal — invite multiple emails to a project with
 * viewer/editor roles. Chip-style email input with paste-splitting
 * on commas/newlines/whitespace.
 */
export default function CollaboratorsModal({ projectId, project, onClose, onUpdate }) {
  const [emailInput, setEmailInput] = useState("");
  const [chips, setChips] = useState([]);
  const [role, setRole] = useState("viewer");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const commitChip = (raw) => {
    const parts = String(raw)
      .split(/[,;\s]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (!parts.length) return;
    setChips((cur) => {
      const seen = new Set(cur.map((c) => c.toLowerCase()));
      const next = [...cur];
      for (const p of parts) {
        const lp = p.toLowerCase();
        if (!seen.has(lp) && /.+@.+\..+/.test(p)) {
          seen.add(lp);
          next.push(p);
        }
      }
      return next;
    });
    setEmailInput("");
  };

  const removeChip = (email) => setChips((cur) => cur.filter((c) => c !== email));

  const removeCollab = async (email) => {
    if (!window.confirm(`Remove ${email} from this project?`)) return;
    try {
      const r = await api.post(`/store/projects/${projectId}/collaborators/remove`, { email });
      toast.success(`Removed ${email}.`);
      onUpdate?.(r.data.collaborators);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove collaborator.");
    }
  };

  const submit = async () => {
    if (chips.length === 0 && emailInput.trim()) commitChip(emailInput);
    // Re-read the chip list after commit (setState is async)
    const finalChips = chips.length
      ? chips
      : (emailInput.trim() ? emailInput.split(/[,;\s]+/).map((s) => s.trim()).filter((s) => /.+@.+\..+/.test(s)) : []);
    if (!finalChips.length) {
      toast.error("Add at least one valid email.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/store/projects/${projectId}/invite`, {
        emails: finalChips,
        role,
        message: message.trim() || null,
        invited_by: project?.email || null,
      });
      const sent = r.data.sent || [];
      const delivered = sent.filter((s) => s.status === "sent").length;
      const logged = sent.filter((s) => s.status === "logged").length;
      if (delivered > 0) {
        toast.success(`Sent ${delivered} invite${delivered === 1 ? "" : "s"}.`);
      } else if (logged > 0) {
        toast.success(`Queued ${logged} invite${logged === 1 ? "" : "s"} (dev mode).`);
      } else {
        toast.success("Invitations recorded.");
      }
      onUpdate?.(r.data.collaborators);
      setChips([]);
      setEmailInput("");
      setMessage("");
    } catch (e) {
      const raw = e?.response?.data?.detail;
      let msg = "Couldn't send invites.";
      if (typeof raw === "string") msg = raw;
      else if (Array.isArray(raw) && raw.length) {
        msg = raw
          .map((r) => (r?.msg ? `${(r.loc || []).slice(-1)[0] || "field"}: ${r.msg}` : String(r)))
          .join(" · ");
      }
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const collabs = project?.collaborators || [];

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      onClick={() => !busy && onClose?.()}
      data-testid="collab-modal"
    >
      <div
        className="bg-[#141110] border border-white/15 rounded-lg max-w-lg w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#D4B878] font-semibold mb-1 flex items-center gap-1.5">
              <UserPlus size={11} /> Project collaborators
            </div>
            <div className="text-lg font-semibold text-white">Invite the rollout team</div>
          </div>
          <button
            onClick={() => !busy && onClose?.()}
            data-testid="collab-modal-close"
            className="text-white/40 hover:text-white"
          >
            <X size={16} />
          </button>
        </div>

        {/* Existing collaborators */}
        {collabs.length > 0 && (
          <div data-testid="collab-existing-list" className="space-y-1.5">
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 font-mono">
              Active ({collabs.length})
            </div>
            {collabs.map((c) => (
              <div
                key={c.id}
                data-testid={`collab-row-${c.email}`}
                className="flex items-center justify-between gap-2 p-2 border border-white/10 rounded bg-white/[0.02]"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-white truncate">{c.email}</div>
                  <div className="text-[10px] text-white/40 font-mono uppercase tracking-[0.12em]">
                    {c.role} · {c.status}
                  </div>
                </div>
                <button
                  onClick={() => removeCollab(c.email)}
                  data-testid={`collab-remove-${c.email}`}
                  className="text-white/40 hover:text-red-300 p-1"
                  title="Remove"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Invite form */}
        <div className="pt-2 border-t border-white/10 space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-white/50 flex items-center gap-1.5">
              <Mail size={10} /> Emails
            </Label>
            <div
              className="mt-1 min-h-[40px] flex flex-wrap items-center gap-1.5 p-1.5 bg-white/5 border border-white/10 rounded"
              data-testid="collab-email-chip-container"
            >
              {chips.map((c) => (
                <span
                  key={c}
                  data-testid={`collab-chip-${c}`}
                  className="text-[11px] font-mono bg-[#D4B878]/15 border border-[#D4B878]/40 text-[#E8D6A3] rounded-full px-2 py-0.5 flex items-center gap-1"
                >
                  {c}
                  <button onClick={() => removeChip(c)} className="hover:text-white">
                    <X size={9} />
                  </button>
                </span>
              ))}
              <input
                data-testid="collab-email-input"
                type="text"
                placeholder={chips.length ? "add another…" : "installer@site.com, brand@agency.com"}
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === ",") {
                    e.preventDefault();
                    commitChip(emailInput);
                  } else if (e.key === "Backspace" && !emailInput && chips.length) {
                    removeChip(chips[chips.length - 1]);
                  }
                }}
                onBlur={() => emailInput.trim() && commitChip(emailInput)}
                onPaste={(e) => {
                  const text = e.clipboardData.getData("text");
                  if (/[,;\s]/.test(text)) {
                    e.preventDefault();
                    commitChip(text);
                  }
                }}
                className="flex-1 min-w-[120px] bg-transparent border-0 outline-none text-sm text-white placeholder:text-white/30 px-1"
              />
            </div>
            <div className="text-[10px] text-white/40 mt-1">
              Press Enter or comma to add. Up to 20 at a time.
            </div>
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-white/50 flex items-center gap-1.5">
              <Shield size={10} /> Role
            </Label>
            <div className="flex gap-1.5 mt-1">
              {[
                { v: "viewer", label: "Viewer", blurb: "See project + BOM" },
                { v: "editor", label: "Editor", blurb: "Add / edit locations" },
              ].map((r) => (
                <button
                  key={r.v}
                  onClick={() => setRole(r.v)}
                  data-testid={`collab-role-${r.v}`}
                  className={`flex-1 text-left rounded border px-3 py-2 transition ${
                    role === r.v
                      ? "border-[#D4B878]/60 bg-[#D4B878]/[0.08] text-white"
                      : "border-white/10 bg-white/[0.02] text-white/60 hover:border-white/25"
                  }`}
                >
                  <div className="text-xs font-semibold">{r.label}</div>
                  <div className="text-[10px] text-white/50">{r.blurb}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-white/50">
              Note (optional)
            </Label>
            <textarea
              data-testid="collab-message-input"
              rows={2}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="e.g. Please confirm your availability for the March 15 install"
              className="w-full mt-1 bg-white/5 border border-white/10 rounded p-2 text-sm text-white placeholder:text-white/30 resize-none"
            />
          </div>

          <Button
            onClick={submit}
            disabled={busy}
            data-testid="collab-send-invites"
            className="w-full rounded-full bg-[#CCFF00] hover:bg-[#B8E600] text-black font-semibold"
          >
            <Send size={13} className="mr-1.5" />
            {busy ? "Sending…" : `Send ${chips.length || (emailInput.trim() ? "1+" : "")} invite${chips.length === 1 ? "" : "s"}`}
          </Button>
        </div>
      </div>
    </div>
  );
}
